import React, { useState, useRef, useEffect } from 'react';
import { Calendar, X } from 'lucide-react';
import { format, subDays } from 'date-fns';
import ModernCalendarModal from './ModernCalendarModal';

const DateFilterBar = ({ activeFilter, onSelectFilter }) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const scrollContainerRef = useRef(null);

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const yesterdayStr = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const sevenDaysAgoStr = format(subDays(new Date(), 7), 'yyyy-MM-dd');

  const presets = [
    { id: 'all', label: 'All Orders', filter: null },
    { id: 'today', label: 'Today', filter: { type: 'today', date: todayStr } },
    { id: 'yesterday', label: 'Yesterday', filter: { type: 'yesterday', date: yesterdayStr } },
    { id: '7days', label: 'Last 7 Days', filter: { type: '7days', startDate: sevenDaysAgoStr, endDate: todayStr } },
  ];

  const isPresetActive = (preset) => {
    if (!activeFilter && preset.id === 'all') return true;
    if (activeFilter?.type === preset.id) return true;
    return false;
  };

  const handleSelectPreset = (presetFilter) => {
    onSelectFilter(presetFilter);
    // Smooth scroll page to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCustomDateSelect = (dateStr) => {
    onSelectFilter({ type: 'custom', date: dateStr });
    // Smooth scroll page to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll chips back to start on reset or when activeFilter becomes null / 'today'
  useEffect(() => {
    if (!activeFilter || activeFilter.type === 'today' || activeFilter.type === 'all') {
      scrollContainerRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [activeFilter]);

  return (
    <>
      {/* Edge-to-edge scrollable container */}
      <div 
        ref={scrollContainerRef}
        className="-mx-4 px-4 flex items-center gap-1.5 overflow-x-auto no-scrollbar [scrollbar-width:none] [&::-webkit-scrollbar]:hidden py-0.5"
      >
        {presets.map((preset) => {
          const active = isPresetActive(preset);
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => handleSelectPreset(preset.filter)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1 shrink-0 cursor-pointer ${
                active 
                  ? 'bg-gray-900 text-white shadow-sm scale-102' 
                  : 'bg-white text-gray-600 border border-gray-200/80 hover:bg-gray-50'
              }`}
            >
              {preset.label}
            </button>
          );
        })}

        {/* Custom Modern Date Picker Trigger */}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setIsCalendarOpen(true)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer border ${
              activeFilter?.type === 'custom'
                ? 'bg-gray-900 text-white border-gray-900 shadow-sm scale-102'
                : 'bg-white text-gray-600 border-gray-200/80 hover:bg-gray-50'
            }`}
          >
            <Calendar size={13} className={activeFilter?.type === 'custom' ? 'text-white' : 'text-gray-400'} />
            <span>
              {activeFilter?.type === 'custom' && activeFilter.date 
                ? format(new Date(activeFilter.date), 'dd MMM yyyy') 
                : 'Pick Date'}
            </span>
          </button>
        </div>

        {/* Clear active filter button */}
        {activeFilter && (
          <button
            type="button"
            onClick={() => {
              handleSelectPreset(null);
              scrollContainerRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
            }}
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0 cursor-pointer"
            title="Clear date filter"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Modern Interactive Calendar Modal */}
      <ModernCalendarModal
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        onSelectDate={handleCustomDateSelect}
        selectedDateStr={activeFilter?.type === 'custom' ? activeFilter.date : ''}
      />
    </>
  );
};

export default DateFilterBar;
